<template>
  <div
    class="mb-4 flex w-full flex-col gap-4 md:flex-row md:items-center md:justify-between"
  >
    <div class="relative flex items-center self-center">
      <ChartHeatmap
        :data="dayHeatmapData"
        :labels="dayHeatmapLabels"
        class="w-80 pt-4"
      />
      <div class="absolute -right-6 top-2">
        <BaseToolTip
          text="Darker squares indicate days containing more transactions with gratuity than lighter squares"
          placement="bottom-start"
        >
          <IconInfo
            class="size-4 text-primary"
            aria-hidden="true"
          />
        </BaseToolTip>
      </div>
    </div>
    <div class="self-center">
      <DateFilter
        :model-value="globalQuery"
        :time-period-label="timePeriodLabel"
        range-type="custom"
        @update:model-value="setGlobalQuery"
      />
    </div>
  </div>

  <QueryError
    v-if="isGratuitySummaryError"
    class="mb-8 min-h-48"
    :show-retry="true"
    @retry="refetchGratuitySummary"
  >
    Failed to retrieve gratuity summary
  </QueryError>
  <template v-else>
    <BaseWarningBox
      v-if="
        gratuitySummaryData?.warnIncorrectCurrencyTransaction
      "
      class="my-4"
    >
      <p>
        Due to the existence of transactions not in
        {{ tenant.currency }} for this time period, some
        transactions will not be accounted for in the data
        on this page.
      </p>
    </BaseWarningBox>

    <h3
      class="mb-2 shrink-0 text-sm font-medium text-background-fg/70"
    >
      Summary
    </h3>
    <div
      class="relative mb-8 grid w-full grid-cols-2 gap-2 sm:grid-cols-4"
    >
      <BaseInnerLoading v-if="isGratuitySummaryLoading" />
      <div
        v-for="summaryItem in summary"
        :key="summaryItem.label"
        class="rounded-lg bg-background p-3 shadow ring-background-fg/8"
      >
        <p
          class="mb-2 line-clamp-1 text-center text-xs font-medium text-background-fg/70"
        >
          {{ summaryItem.label }}
        </p>

        <div class="flex items-center justify-center">
          <span
            class="line-clamp-1 text-2xl font-bold text-primary"
            >{{ summaryItem.value }}</span
          >
        </div>
      </div>
    </div>
  </template>

  <BaseCard class="relative mb-8">
    <QueryLoading
      v-if="showGratuityChartLoading"
      class="min-h-96"
    />
    <QueryError
      v-else-if="showGratuityChartError"
      class="min-h-96"
      :show-retry="true"
      @retry="refetchGratuityChart"
    >
      Failed to retrieve gratuity report
    </QueryError>
    <div v-else class="flex w-full flex-col">
      <div class="flex justify-end py-2">
        <ToggleSwitch
          v-model="graphView"
          :options="graphViewOptions"
          selected-option-color="primary"
          text-size="sm"
        />
      </div>
      <div
        class="h-64 w-full overflow-hidden sm:h-96 md:h-[32rem]"
      >
        <template v-if="datasets[0].data.length > 0">
          <ChartBarDate
            v-if="graphView === 'trend'"
            id="transactions-trend-graph"
            :labels="labels"
            :datasets="datasets"
            :granularity="granularity"
            :currency="tenant.currency"
            @hover-datapoint="handleHoverDatapoint"
          />
          <ChartLineDate
            v-else-if="graphView === 'cumulative'"
            id="transactions-cumulative-graph"
            :labels="labels"
            :datasets="datasets"
            :granularity="granularity"
            is-cumulative
            :currency="tenant.currency"
            @hover-datapoint="handleHoverDatapoint"
          />
        </template>
      </div>

      <div
        v-if="hoveredData"
        class="shrink-0 rounded-lg bg-background-fg/5 p-4"
      >
        <div class="mb-2">
          <div class="text-sm text-background-fg/70">
            {{ hoveredData.formattedLabel }}
          </div>
        </div>
        <div
          v-for="dataset in hoveredData.datasets"
          :key="dataset.color"
          class="grid auto-rows-fr grid-cols-1 gap-4 sm:grid-cols-2"
        >
          <div class="flex w-full items-center space-x-4">
            <ColorItem :value="dataset.color" rounded />
            <div
              class="flex flex-1 flex-wrap items-center justify-start gap-2"
            >
            </div>
            <div class="shrink-0">
              <p
                class="text-center text-sm leading-tight text-background-fg/70"
              >
                Gratuity Received
              </p>
              <p class="text-center font-semibold">
                {{
                  formatToMerchantCurrencyString(
                    dataset.value
                  )
                }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </BaseCard>

  <BaseCard class="relative">
    <QueryLoading
      v-if="isCashierGratuityStateLoading"
      class="min-h-96"
    />
    <QueryError
      v-else-if="isCashierGratuityStateError"
      class="min-h-96"
      :show-retry="true"
      @retry="refetchGratuityReportData"
    >
      Failed to retrieve gratuity cashiers
    </QueryError>
    <template v-else>
      <div
        class="mb-2 flex w-full flex-col-reverse gap-2 md:flex-row md:items-center md:justify-between"
      >
        <SearchInput
          v-model="rawSearch"
          class="w-full md:w-auto"
        />
        <span
          class="self-end pr-2 text-right text-sm text-background-fg/70 md:mr-3 md:self-auto md:pr-0"
        >
          {{ timePeriodLabel }}
        </span>
      </div>
      <BaseTable
        v-model:ordering="cashierGratuityStatsOrdering"
        :rows="cashierGratuityStatsData?.items ?? []"
        :columns="columns"
        :is-loading="isCashierGratuityStateFetching"
      />

      <div class="flex justify-end py-2">
        <BasePagination
          v-model:page="cashierGratuityStatsPage"
          :page-count="cashierGratuityStatsPageCount"
        />
      </div>
    </template>
  </BaseCard>
</template>

<script setup lang="ts">
import {
  type TableOrdering,
  type TableColumn,
} from "@/components/BaseTable.vue"
import type {
  ListCashierGratuityStatsFilter,
  ListCashierGratuityStatsOrdering,
  ListCashierGratuityStatsResponseItem,
} from "@/services/reports/listCashierGratuityStats"
import type { HoveredDataPoint } from "@/components/ChartLineDate.vue"
import type { GratuityReportQuery } from "@/services/reports/retrieveGratuityReport"

const { formatToMerchantCurrencyString } = useCurrency()
const { tenant } = useTenant()

const {
  apiQueries: gratuityChartQueries,
  localQueries,
  globalQuery,
  setGlobalQuery,
  timePeriodLabel,
} = useReportQuery<GratuityReportQuery>({})

const rawSearch = ref("")

const debouncedSearch = debouncedRef(rawSearch, 300)

const cashierGratuityStatsFilter =
  computed<ListCashierGratuityStatsFilter>(() => ({
    search: debouncedSearch.value.trim(),
  }))

const {
  page: cashierGratuityStatsPage,
  pageCount: cashierGratuityStatsPageCount,
  paginationQuery: cashierGratuityStatsPagintion,
  resetPagination: resetCashierGratuityStatsPagination,
} = usePagination({
  count: computed(
    () => cashierGratuityStatsData.value?.count || 0
  ),
  limit: 10,
})

const cashierGratuityStatsOrdering = ref<
  | TableOrdering<ListCashierGratuityStatsOrdering>
  | undefined
>()

watch(
  () => cashierGratuityStatsFilter,
  () => resetCashierGratuityStatsPagination(),
  { deep: true }
)

const cashierGratuityReportquery = reactiveComputed(() => ({
  ...cashierGratuityStatsFilter.value,
  ordering: cashierGratuityStatsOrdering.value,
  ...cashierGratuityStatsPagintion.value,
  ...globalQuery.value,
}))

const {
  data: gratuitySummaryData,
  isError: isGratuitySummaryError,
  isLoading: isGratuitySummaryLoading,
  refetch: refetchGratuitySummary,
} = useGratuitySummary(globalQuery)

const { data: gratuityHeatmapData } = useGratuityHeatmap()

const gratuityChartData = useGratuityReport(
  gratuityChartQueries
)

const showGratuityChartLoading = computed(() =>
  gratuityChartData.some((query) => query.isLoading)
)

const showGratuityChartError = computed(() =>
  gratuityChartData.some((query) => query.isError)
)

const refetchGratuityChart = async () => {
  if (gratuityChartData.length === 0) return
  await Promise.all(
    gratuityChartData.map((query) => query.refetch())
  )
}

const rawDatasets = computed(() =>
  gratuityChartData.map(({ data }) => data?.dataset)
)

const granularity = computed(
  () => gratuityChartData?.[0]?.data?.granularity
)

const labels = computed(
  () =>
    rawDatasets.value?.[0]?.map(({ start, end }) => ({
      start,
      end,
    })) || []
)

const datasets = computed(() =>
  rawDatasets.value.map((rawDataset, index) => {
    const colorVariableName =
      localQueries[index].colorVariableName
    const colorVariable = colorVariableName
      ? useCssVar(colorVariableName).value
      : undefined
    const color = colorVariable
      ? `rgb(${colorVariable})`
      : undefined
    return {
      label: "",
      data: rawDataset?.map(({ value }) => value) || [],
      color: color,
    }
  })
)

const handleHoverDatapoint = (
  hoveredData_: HoveredDataPoint
) => {
  hoveredData.value = hoveredData_
}

const hoveredData = ref<HoveredDataPoint>()

type GraphView = "trend" | "cumulative"
const graphView = ref<GraphView>("trend")
const graphViewOptions = [
  { label: "Trend", value: "trend" },
  { label: "Cumulative", value: "cumulative" },
] as const

type ColumnId =
  | "cashierUsername"
  | "gratuityReceived"
  | "averageGratuityPercentage"
  | "serviceChargeTaken"

const columns: Array<
  TableColumn<
    ListCashierGratuityStatsResponseItem,
    ColumnId,
    ListCashierGratuityStatsOrdering
  >
> = [
  {
    id: "cashierUsername",
    header: "Cashier",
    getCellValue: (row) => row.cashierUsername,
    isSortable: true,
    autoWidth: true,
  },
  {
    id: "gratuityReceived",
    header: "Gratuity Received",
    getCellValue: (row) =>
      formatToMerchantCurrencyString(row.gratuityReceived),
    isSortable: true,
  },
  {
    id: "averageGratuityPercentage",
    header: "Average Gratuity Percentage",
    getCellValue: (row) =>
      `${row.averageGratuityPercentage}%`,
    isSortable: true,
  },
  {
    id: "serviceChargeTaken",
    header: "Service Charge Taken",
    getCellValue: (row) =>
      formatToMerchantCurrencyString(
        row.serviceChargeTaken
      ),
    autoWidth: true,
    isSortable: true,
  },
]

const {
  data: cashierGratuityStatsData,
  isError: isCashierGratuityStateError,
  isLoading: isCashierGratuityStateLoading,
  isFetching: isCashierGratuityStateFetching,
  refetch: refetchGratuityReportData,
} = useCashierGratuityStats(cashierGratuityReportquery)

const summary = computed(() =>
  gratuitySummaryData.value
    ? {
        gratuityReceived: {
          label: "Gratuity Received",
          value: formatToMerchantCurrencyString(
            gratuitySummaryData.value.gratuityReceived
          ),
        },
        averageGratuityPercentage: {
          label: "Average Gratuity Percentage",
          value: `${gratuitySummaryData.value.averageGratuityPercentage}%`,
        },
        fractionOfTransactionsWithGratuity: {
          label: "Transactions With Gratuity",
          value:
            gratuitySummaryData.value
              .fractionOfTransactionsWithGratuity === "0/0"
              ? "0"
              : gratuitySummaryData.value
                  .fractionOfTransactionsWithGratuity,
        },
        totalServiceCharge: {
          label: "Total Service Charge",
          value: formatToMerchantCurrencyString(
            gratuitySummaryData.value.totalServiceCharge
          ),
        },
      }
    : {
        gratuityReceived: {
          label: "Gratuity Received",
          value: "-",
        },
        averageGratuityPercentage: {
          label: "Average Gratuity Percentage",
          value: "-",
        },
        fractionOfTransactionsWithGratuity: {
          label: "Transactions With Gratuity",
          value: "-",
        },
        totalServiceCharge: {
          label: "Total Service Charge",
          value: "-",
        },
      }
)

const ISO_DAYS = {
  1: "Mo",
  2: "Tu",
  3: "We",
  4: "Th",
  5: "Fr",
  6: "Sa",
  7: "Su",
}

const dayHeatmapData = computed(
  () =>
    gratuityHeatmapData.value?.data.map(
      ({ dayOfWeek, transactionCount }) => ({
        x: ISO_DAYS[dayOfWeek],
        y: transactionCount,
      })
    ) || []
)
const dayHeatmapLabels = Object.values(ISO_DAYS)
</script>
